import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Button,
    Typography,
    Avatar,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from "@mui/material";
import { Print, ArrowBack } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import AuthService from "../Auth/AuthService";
import { printHtml } from "../utils/htmlToPrint";

const TimetableView = () => {
    const navigate = useNavigate();

    const [entryList, setEntryList] = useState([]);
    const [loading, setLoading] = useState(true);
    const hasFetched = useRef(false);

    const [academicYear, setAcademicYear] = useState("");

    const schoolName = localStorage.getItem("name");
    const logoUrl = localStorage.getItem("logoUrl");
    const contactPhone = sessionStorage.getItem("phone");

    const [adaddress, setAddress] = useState(null);

    useEffect(() => {
        try {
            const schoolData = localStorage.getItem("address");

            if (schoolData) {
                setAddress(JSON.parse(schoolData));
            }
        } catch (error) {
            console.error(
                "Error reading school address:",
                error
            );
        }
    }, []);

    useEffect(() => {
        if (hasFetched.current) return;

        hasFetched.current = true;

        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);

            const url = AuthService.pathUrl("GET", "/entry-card");

            const response = await AuthService.getAuth(url);

            const data = response || [];

            if (Array.isArray(data)) {
                setEntryList(data);

                if (data.length > 0) {
                    setAcademicYear(
                        data[0].academicYear || ""
                    );
                }
            } else {
                console.error(
                    "Entry Card response is not an array:",
                    data
                );

                setEntryList([]);
            }
        } catch (error) {
            console.error(
                "Error fetching entry card:",
                error
            );

            setEntryList([]);

            alert(
                "Error while loading Entry Card."
            );
        } finally {
            setLoading(false);
        }
    };

    const formatTime = (time) => {
        if (!time) return "-";

        const [hours, minutes] =
            time.split(":");

        const date = new Date();

        date.setHours(
            Number(hours),
            Number(minutes),
            0,
            0
        );

        return date.toLocaleTimeString(
            "en-US",
            {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
            }
        );
    };

    const formatDate = (dateString) => {
        if (!dateString) return "-";

        const [year, month, day] =
            dateString.split("-");

        return `${day}-${month}-${year}`;
    };

    const groupedByStandard = entryList.reduce(
        (groups, item) => {
            const standard =
                item.standard?.trim() ||
                "Unknown";

            if (!groups[standard]) {
                groups[standard] = [];
            }

            groups[standard].push(item);

            return groups;
        },
        {}
    );

    const standards =
        Object.keys(groupedByStandard);

    const standardPages = []; for (let i = 0; i < standards.length; i += 2) { standardPages.push(standards.slice(i, i + 2)); }

    const handlePrint = () => {
        printHtml("entry-card");
        window.location.reload();
    };

    return (
        <Box
            sx={{
                p: 3,
                backgroundColor: "#f5f5f5",
                minHeight: "100vh",

                "@media print": {
                    p: 0,
                    backgroundColor: "#fff",
                },
            }}
        >
            {/* Buttons */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent:
                        "space-between",
                    mb: 2,

                    "@media print": {
                        display: "none",
                    },
                }}
            >
                <Button
                    startIcon={<ArrowBack />}
                    variant="outlined"
                    onClick={() => navigate(-1)}
                >
                    Back
                </Button>

                <Button
                    startIcon={<Print />}
                    variant="contained"
                    onClick={handlePrint}
                    disabled={
                        loading ||
                        entryList.length === 0
                    }
                    sx={{
                        bgcolor: "#1b1914",
                        borderRadius: 5,
                    }}
                >
                    Print
                </Button>
            </Box>

            {/* Printable Area */}
            <Box id="entry-card">
                {loading ? (
                    <Box
                        sx={{
                            py: 5,
                            textAlign: "center",
                        }}
                    >
                        <Typography>
                            Loading examination
                            schedule...
                        </Typography>
                    </Box>
                ) : entryList.length === 0 ? (
                    <Box
                        sx={{
                            py: 5,
                            textAlign: "center",
                        }}
                    >
                        <Typography
                            variant="h6"
                            color="text.secondary"
                        >
                            No Entry Card data found.
                        </Typography>
                    </Box>
                ) : (
                    standardPages.map((pageStandards, pageIndex) => (
                        <Box
                            key={pageIndex}
                            sx={{
                                maxWidth: 900,
                                mx: "auto",
                                backgroundColor: "#fff",
                                border: "2px solid #222",
                                p: 3,
                                mb: 3,

                                "@media print": {
                                    maxWidth: 700,
                                    width: "100%",
                                    // minHeight: "100vh",
                                    border: "2px solid #222",
                                    p: 2,
                                    m: 0,

                                    pageBreakAfter:
                                        pageIndex < standardPages.length - 1
                                            ? "always"
                                            : "auto",
                                },
                            }}
                        >

                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                    mb: 2,
                                }}
                            >
                                {logoUrl && (
                                    <Avatar
                                        src={logoUrl}
                                        alt="School Logo"
                                        sx={{
                                            width: 75,
                                            height: 75,
                                            borderRadius: 50,
                                        }}
                                    />
                                )}

                                <Box
                                    sx={{
                                        flex: 1,
                                        textAlign: "center",
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 800,
                                            textTransform: "uppercase",
                                        }}
                                    >
                                        {schoolName || "School Name"}
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        sx={{ mt: 0.5 }}
                                    >
                                        {[
                                            adaddress?.addressLine1,
                                            adaddress?.city,
                                            adaddress?.state,
                                        ]
                                            .filter(Boolean)
                                            .join(", ")}
                                    </Typography>

                                    {contactPhone && (
                                        <Typography variant="body2">
                                            Tel: {contactPhone}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>

                            <Divider
                                sx={{
                                    borderColor: "#222",
                                    mb: 2,
                                }}
                            />

                            <Typography
                                variant="subtitle2"
                                align="center"
                                sx={{
                                    fontWeight: 800,
                                    textTransform: "uppercase",
                                    mb: 2,
                                    textDecoration: "underline",
                                }}
                            >
                                Examination Time Table
                            </Typography>

                            {pageStandards.map((standard, index) => {
                                const standardEntries =
                                    groupedByStandard[standard];

                                const startTime =
                                    standardEntries[0]?.startTime || "";

                                const endTime =
                                    standardEntries[0]?.endTime || "";

                                return (
                                    <Box
                                        key={standard}
                                        sx={{
                                            mb:
                                                index <
                                                    pageStandards.length - 1
                                                    ? 3
                                                    : 0,

                                            pb:
                                                index <
                                                    pageStandards.length - 1
                                                    ? 3
                                                    : 0,

                                            borderBottom:
                                                index <
                                                    pageStandards.length - 1
                                                    ? "1px dashed #999"
                                                    : "none",
                                        }}
                                    >
                                        {/* Standard + Time */}
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent:
                                                    "space-between",
                                                mb: 1,
                                            }}
                                        >
                                            <Typography
                                                variant="subtitle2"
                                                fontWeight="bold"
                                            >
                                                Examination Schedule:{" "}
                                                {academicYear}
                                            </Typography>

                                            <Typography
                                                variant="subtitle2"
                                                fontWeight="bold"
                                            >
                                                Standard: {standard}
                                                {", Time: "}
                                                {formatTime(startTime)}
                                                {" - "}
                                                {formatTime(endTime)}
                                            </Typography>
                                        </Box>

                                        {/* Table */}
                                        <TableContainer
                                            component={Paper}
                                            elevation={0}
                                        >
                                            <Table
                                                size="small"
                                                sx={{
                                                    border:
                                                        "1px solid #777",

                                                    "& th, & td": {
                                                        border:
                                                            "1px solid #777",
                                                        padding:
                                                            "6px 8px",
                                                    },

                                                    "& th": {
                                                        fontWeight: 700,
                                                        backgroundColor:
                                                            "#f5f5f5",
                                                    },
                                                }}
                                            >
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell align="center">
                                                            <strong>
                                                                Sr. No.
                                                            </strong>
                                                        </TableCell>

                                                        <TableCell align="center">
                                                            <strong>
                                                                Subject
                                                            </strong>
                                                        </TableCell>

                                                        <TableCell align="center">
                                                            <strong>
                                                                Day
                                                            </strong>
                                                        </TableCell>

                                                        <TableCell align="center">
                                                            <strong>
                                                                Exam Date
                                                            </strong>
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>

                                                <TableBody>
                                                    {standardEntries.map(
                                                        (item, index) => (
                                                            <TableRow
                                                                key={
                                                                    item.id ||
                                                                    index
                                                                }
                                                            >
                                                                <TableCell align="center">
                                                                    {index + 1}
                                                                </TableCell>

                                                                <TableCell align="center">
                                                                    {item.subject}
                                                                </TableCell>

                                                                <TableCell align="center">
                                                                    {item.days}
                                                                </TableCell>

                                                                <TableCell align="center">
                                                                    {formatDate(
                                                                        item.examDate
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                        )
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                );
                            })}

                            <Box
                                sx={{
                                    mt: 3,
                                    p: 1.5,
                                    border: "1px solid #999",
                                }}
                            >
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        fontWeight: 700,
                                        mb: 0.5,
                                    }}
                                >
                                    Note:
                                </Typography>

                                <Typography variant="body2">
                                    The admit card is essential and
                                    must be carried to the exam centre.
                                    Candidates may also need to carry a
                                    valid photo ID proof.
                                </Typography>
                            </Box>

                            {/* <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    mt: 5,
                                }}
                            >
                                <Box
                                    sx={{
                                        textAlign: "center",
                                        minWidth: 150,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            borderBottom:
                                                "1px solid #222",
                                            mb: 1,
                                        }}
                                    />

                                    <Typography
                                        variant="body2"
                                        fontWeight={600}
                                    >
                                        Class Teacher
                                    </Typography>
                                </Box>

                                <Box
                                    sx={{
                                        textAlign: "center",
                                        minWidth: 150,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            borderBottom:
                                                "1px solid #222",
                                            mb: 1,
                                        }}
                                    />

                                    <Typography
                                        variant="body2"
                                        fontWeight={600}
                                    >
                                        Principal
                                    </Typography>
                                </Box>
                            </Box> */}

                        </Box>
                    ))
                )}
            </Box>
        </Box>
    );
};

export default TimetableView;
